import React from 'react';
import { listarVentasOffline, resumenVentasOffline } from './offlineDb';
import { descargarDatosOffline, sincronizarPendientes, reintentarVentaOffline } from './offlineManager';
import { respaldoVentasPendientes } from './offlineRevision';

function colorEstado(estado) {
  if (estado === 'conflicto') return '#dc2626';
  if (estado === 'sincronizada') return '#059669';
  if (estado === 'sincronizando') return '#2563eb';
  return '#d97706';
}

export default function OfflineStatus({ usuario, esMovil = false }) {
  const offlineSoportado = typeof indexedDB !== 'undefined';
  const [online, setOnline] = React.useState(() => navigator.onLine);
  const [resumen, setResumen] = React.useState({ pendientes: 0, conflictos: 0, sincronizadas: 0, total: 0 });
  const [ventas, setVentas] = React.useState([]);
  const [abierto, setAbierto] = React.useState(false);
  const [sincronizando, setSincronizando] = React.useState(false);
  const [descargando, setDescargando] = React.useState(false);
  const [instalable, setInstalable] = React.useState(Boolean(window.__pwaInstallPrompt));
  const [mensaje, setMensaje] = React.useState('');

  const refrescar = React.useCallback(async () => {
    if (!usuario || !offlineSoportado) return;
    try {
      const [r, lista] = await Promise.all([
        resumenVentasOffline(usuario), listarVentasOffline({ usuario, incluirSincronizadas: true }),
      ]);
      setResumen(r); setVentas(lista);
    } catch { setMensaje('No se pudieron leer las ventas guardadas. Reintentá antes de cerrar la caja.'); }
  }, [usuario, offlineSoportado]);

  const sincronizar = React.useCallback(async () => {
    if (!usuario || !offlineSoportado || !navigator.onLine || sincronizando) return;
    setSincronizando(true); setMensaje('');
    try {
      const r = await sincronizarPendientes(usuario);
      if (r.sincronizadas) setMensaje(`${r.sincronizadas} venta(s) sincronizada(s).`);
      if (r.conflictos) setMensaje(`${r.conflictos} venta(s) requieren revisión.`);
    } catch (e) {
      setMensaje(e?.mensaje || e?.message || 'No se pudieron sincronizar las ventas. Se conservan para reintentar.');
    } finally {
      setSincronizando(false); await refrescar();
    }
  }, [usuario, offlineSoportado, sincronizando, refrescar]);

  React.useEffect(() => {
    if (!offlineSoportado) return undefined;
    const onOnline = () => { setOnline(true); setTimeout(() => sincronizar(), 500); };
    const onOffline = () => setOnline(false);
    const onQueue = () => refrescar();
    const onInstall = () => setInstalable(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('offline-queue-changed', onQueue);
    window.addEventListener('offline-sync-finished', onQueue);
    window.addEventListener('pwa-install-available', onInstall);
    refrescar();
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('offline-queue-changed', onQueue);
      window.removeEventListener('offline-sync-finished', onQueue);
      window.removeEventListener('pwa-install-available', onInstall);
    };
  }, [refrescar, sincronizar, offlineSoportado]);

  React.useEffect(() => {
    if (offlineSoportado && online && usuario) sincronizar();
    // solo al cambiar contexto/usuario o recuperar conexión
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online, usuario?.id, usuario?.empresa_id, usuario?.sucursal_id, usuario?.deposito_id]);

  const prepararOffline = async () => {
    if (!offlineSoportado) return;
    setDescargando(true); setMensaje('');
    try {
      await descargarDatosOffline(usuario);
      setMensaje('Catálogo, stock y configuración disponibles sin conexión.');
    } catch (e) {
      setMensaje(e?.mensaje || 'No se pudieron descargar los datos offline.');
    } finally { setDescargando(false); }
  };

  const instalar = async () => {
    const prompt = window.__pwaInstallPrompt;
    if (!prompt) return;
    await prompt.prompt();
    await prompt.userChoice.catch(() => null);
    window.__pwaInstallPrompt = null;
    setInstalable(false);
  };

  const descargarPendientes = async () => {
    try {
      const lista = await listarVentasOffline({ usuario, incluirSincronizadas: false });
      const url = URL.createObjectURL(new Blob([JSON.stringify(respaldoVentasPendientes(lista), null, 2)], { type: 'application/json' }));
      const enlace = document.createElement('a');
      enlace.href = url; enlace.download = `ventas-pendientes-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(enlace); enlace.click(); enlace.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setMensaje('Copia descargada para revisión. Los comprobantes siguen guardados aquí.');
    } catch { setMensaje('No se pudo descargar la copia. Las ventas siguen guardadas en este dispositivo.'); }
  };

  if (!offlineSoportado) return null;

  const pendientes = resumen.pendientes + resumen.conflictos;
  const fondo = !online ? '#fff7ed' : resumen.conflictos ? '#fef2f2' : pendientes ? '#eff6ff' : '#ecfdf5';
  const color = !online ? '#c2410c' : resumen.conflictos ? '#dc2626' : pendientes ? '#1d4ed8' : '#047857';
  const texto = !online ? `Sin conexión${pendientes ? ` · ${pendientes}` : ''}` : sincronizando ? 'Sincronizando…' : resumen.conflictos ? `Conflictos · ${resumen.conflictos}` : pendientes ? `Pendientes · ${pendientes}` : 'En línea';

  return <>
    <button onClick={() => setAbierto(true)} title="Estado PWA y sincronización"
      style={{background:fondo,color,border:`1px solid ${color}40`,padding:'6px 11px',borderRadius:'18px',fontSize:'12px',fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
      {online ? '●' : '○'} {!esMovil && texto}
      {esMovil && pendientes > 0 ? pendientes : ''}
    </button>

    {abierto && <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,.58)',zIndex:5000,display:'flex',justifyContent:'center',alignItems:'center',padding:'16px'}} onClick={() => setAbierto(false)}>
      <div onClick={(e)=>e.stopPropagation()} style={{background:'#fff',borderRadius:'16px',width:'min(760px,96vw)',maxHeight:'88vh',overflow:'auto',boxShadow:'0 24px 70px rgba(0,0,0,.25)'}}>
        <div style={{padding:'18px 20px',borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div><h3 style={{margin:0}}>PWA y sincronización</h3><div style={{fontSize:'12px',color:'#64748b',marginTop:'4px'}}>{online?'Conexión disponible':'Trabajando con datos guardados en este dispositivo'}</div></div>
          <button onClick={()=>setAbierto(false)} style={{border:0,background:'transparent',fontSize:'22px',cursor:'pointer'}}>×</button>
        </div>
        <div style={{padding:'18px 20px'}}>
          <div style={{display:'flex',gap:'10px',flexWrap:'wrap',marginBottom:'16px'}}>
            <button disabled={!online||descargando} onClick={prepararOffline} style={boton('#0f766e')}>{descargando?'Descargando…':'Descargar datos para uso offline'}</button>
            <button disabled={!online||sincronizando||resumen.pendientes===0} onClick={sincronizar} style={boton('#2563eb')}>{sincronizando?'Sincronizando…':'Sincronizar ahora'}</button>
            <button onClick={descargarPendientes} style={boton('#475569')}>Descargar pendientes para revisión</button>
            {instalable && <button onClick={instalar} style={boton('#7c3aed')}>Instalar aplicación</button>}
          </div>
          {mensaje && <div style={{padding:'10px 12px',borderRadius:'8px',background:'#f1f5f9',color:'#334155',marginBottom:'14px'}}>{mensaje}</div>}
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,minmax(0,1fr))',gap:'10px',marginBottom:'16px'}}>
            <Resumen label="Pendientes" valor={resumen.pendientes} color="#d97706"/><Resumen label="Conflictos" valor={resumen.conflictos} color="#dc2626"/><Resumen label="Sincronizadas" valor={resumen.sincronizadas} color="#059669"/>
          </div>
          <h4 style={{margin:'8px 0'}}>Ventas guardadas en este contexto</h4>
          {ventas.length===0 ? <p style={{color:'#64748b'}}>No hay ventas offline guardadas.</p> : ventas.slice().reverse().map((v)=><div key={v.idempotency_key} style={{border:'1px solid #e2e8f0',borderLeft:`5px solid ${colorEstado(v.estado)}`,borderRadius:'9px',padding:'10px 12px',marginBottom:'8px'}}>
            <div style={{display:'flex',justifyContent:'space-between',gap:'12px',alignItems:'start'}}>
              <div><strong>{new Date(v.creada_en).toLocaleString('es-AR')}</strong><div style={{fontSize:'12px',color:'#64748b'}}>Local OFF-{String(v.secuencia).padStart(6,'0')} · {v.cliente_nombre}</div></div>
              <div style={{textAlign:'right'}}><strong>${Number(v.preview?.total_final||0).toFixed(2)}</strong><div style={{fontSize:'12px',fontWeight:700,color:colorEstado(v.estado)}}>{v.estado}</div></div>
            </div>
            {v.error && <div style={{fontSize:'12px',color:'#b91c1c',marginTop:'7px'}}>{v.error}</div>}
            {v.server_result?.ticket_numero && <div style={{fontSize:'12px',color:'#047857',marginTop:'7px'}}>Ticket oficial: {v.server_result.ticket_serie}-{String(v.server_result.ticket_numero).padStart(8,'0')}</div>}
            {v.estado==='conflicto' && <div style={{display:'flex',gap:'8px',marginTop:'8px'}}><button disabled={sincronizando} onClick={async()=>{await reintentarVentaOffline(v.idempotency_key);await refrescar();}} style={miniBoton}>Reintentar</button><span style={{fontSize:'12px',color:'#64748b'}}>Conservada para revisión del encargado.</span></div>}
          </div>)}
          <div style={{fontSize:'12px',color:'#64748b',marginTop:'14px',lineHeight:1.5}}>Las ventas offline se guardan únicamente en este dispositivo. ARCA no emite CAE sin conexión; la facturación queda pendiente hasta recuperar internet.</div>
        </div>
      </div>
    </div>}
  </>;
}

function Resumen({label,valor,color}) { return <div style={{background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:'9px',padding:'10px'}}><div style={{fontSize:'12px',color:'#64748b'}}>{label}</div><div style={{fontSize:'22px',fontWeight:800,color}}>{valor}</div></div>; }
const boton=(background)=>({background,color:'#fff',border:0,borderRadius:'8px',padding:'9px 12px',fontWeight:700,cursor:'pointer'});
const miniBoton={background:'#fff',border:'1px solid #cbd5e1',borderRadius:'6px',padding:'5px 8px',cursor:'pointer',fontSize:'12px'};
